import { FC, useCallback, useMemo, useState } from 'react';
import Avatar from 'antd/es/avatar/avatar';
import Card from 'antd/es/card/Card';
import Divider from 'antd/es/divider';
import Flex from 'antd/es/flex';
import Text from 'antd/es/typography/Text';
import Title from 'antd/es/typography/Title';
import { MButton } from '@Components/Base';
import {
  useGetTenderRecommendationQuery,
  usePostTenderRemarkMutation,
} from '@Features/MaterialRequest/requests/TenderRequest/store/api/TenderRequestApiSlice';
import { useParams } from 'react-router-dom';
import { parseDateTime } from '@Utils/transformDateTime';
import { DATE_FORMAT } from '@Components/ApprovalHistory/constants';
import useUserPermissions from '@Hooks/useUserPermissions';
import { ButtonsFooter } from '../../../common/ButtonsFooter';
import { AddRemarks } from '../../AddRemarks';
import { AddRemarksFormValues } from '../../AddRemarks/AddRemarks';
import { TenderVendorRecommedationOccupation, VendorRemark } from './types';

import styles from './Remarks.module.css';

interface RemarkProps {
  remark: VendorRemark;
}
/**
 * @todo move to commons
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const RemarkCard: FC<RemarkProps> = ({ remark }) => {
  const { name, value, date, occupation } = remark;

  return (
    <Card className={styles.remarkCard}>
      <Flex vertical>
        <Flex gap="16px" align="center">
          <Flex vertical flex="0 0 auto">
            <Avatar />
          </Flex>
          <Flex vertical flex="1 0 auto">
            <Text strong>{name}</Text>
            <Text>
              {date && name ? 'Approved' : 'Remarks'} by {occupation}
            </Text>
          </Flex>
          <Flex vertical flex="0 0 auto">
            <Text type="secondary">{date}</Text>
          </Flex>
        </Flex>
        <Divider style={{ margin: '16px 0' }} />
        <Text>{value || 'All remarks goes here'}</Text>
      </Flex>
    </Card>
  );
};

const Remarks: FC<{ vendorId: number }> = ({ vendorId }) => {
  const { tenderId } = useParams();
  const [addRemarksShown, setAddRemarksShown] = useState<boolean>(false);
  const permissions = useUserPermissions();

  const [postRemark] = usePostTenderRemarkMutation();

  const { data: recommendations } = useGetTenderRecommendationQuery(
    {
      tenderId: Number(tenderId),
      vendorId,
    },
    { skip: !tenderId }
  );

  const recommendationRemarks = useMemo(() => {
    return recommendations?.map((rec) => ({
      value: rec.remark,
      date: parseDateTime(rec.remarkDate, DATE_FORMAT),
      occupation: TenderVendorRecommedationOccupation[rec.type],
      name: rec.authorName,
    }));
  }, [recommendations]);

  const customButtons = (
    <ButtonsFooter>
      <MButton type="primary" onClick={() => setAddRemarksShown(true)}>
        Add Remarks
      </MButton>
    </ButtonsFooter>
  );

  const handleAddRemark = useCallback(
    (values: AddRemarksFormValues) => {
      if (tenderId) {
        const requestBody = {
          tenderId: Number(tenderId),
          vendorId,
          message: values.remarks,
        };

        postRemark(requestBody).unwrap();
      }
      setAddRemarksShown(false);
    },
    [postRemark, tenderId, vendorId]
  );

  if (!recommendationRemarks) return null;

  return (
    <>
      <Title level={4}>Remarks History</Title>
      <Flex vertical gap="16px">
        {recommendationRemarks.map((remark) => {
          return <RemarkCard remark={remark} />;
        })}
      </Flex>
      {addRemarksShown && (
        <AddRemarks
          onSubmit={handleAddRemark}
          onCancel={() => setAddRemarksShown(false)}
        />
      )}
      {permissions.can('AddRemark', 'TenderReply') ? customButtons : null}
    </>
  );
};

export default Remarks;
